import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import PostCard from '../components/post/PostCard';
import { Search, Users, FileText, Hash, TrendingUp } from 'lucide-react';

export default function SearchPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  
  const [query, setQuery] = useState(initialQuery);
  const [activeTab, setActiveTab] = useState('all');
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (initialQuery) {
      handleSearch(initialQuery);
    }
  }, []);

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setSearched(true);
    setSearchParams({ q: searchQuery });

    try {
      const [usersRes, postsRes, hashtagsRes] = await Promise.all([
        api.get(`/search/users?q=${searchQuery}`),
        api.get(`/search/posts?q=${searchQuery}`),
        api.get(`/search/hashtags?q=${searchQuery}`)
      ]);

      setUsers(usersRes.data);
      setPosts(postsRes.data);
      setHashtags(hashtagsRes.data);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const handlePostUpdate = (postId, updates) => {
    setPosts(prev => prev.map(post => 
      post.id === postId ? { ...post, ...updates } : post
    ));
  };

  return (
    <div className="max-w-3xl mx-auto px-4 min-[900px]:ml-60" data-testid="search-page">
      {/* Search Header */}
      <div className="py-6">
        <h1 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
          <Search className="w-6 h-6 text-primary" />
          Search
        </h1>

        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search users, posts, hashtags..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-12 h-12 rounded-full bg-muted/50 border-transparent text-base"
              data-testid="search-input"
            />
          </div>
          <Button 
            type="submit" 
            className="rounded-full h-12 px-6"
            disabled={!query.trim() || loading}
            data-testid="search-btn"
          >
            Search
          </Button>
        </form>
      </div>

      {/* Results */}
      {searched && (
        <Tabs defaultValue="all" className="mt-4" onValueChange={setActiveTab}>
          <TabsList className="w-full bg-muted/50 p-1 rounded-full mb-6 grid grid-cols-4">
            <TabsTrigger 
              value="all" 
              className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              All
            </TabsTrigger>
            <TabsTrigger 
              value="users" 
              className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <Users className="w-4 h-4 mr-1" />
              Users
            </TabsTrigger>
            <TabsTrigger 
              value="posts" 
              className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <FileText className="w-4 h-4 mr-1" />
              Posts
            </TabsTrigger>
            <TabsTrigger 
              value="hashtags" 
              className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <Hash className="w-4 h-4 mr-1" />
              Tags
            </TabsTrigger>
          </TabsList>

          {/* All Results */}
          <TabsContent value="all" className="space-y-6 pb-8">
            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-20 rounded-xl" />
                ))}
              </div>
            ) : (
              <>
                {/* Users Preview */}
                {users.length > 0 && (
                  <div>
                    <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      People
                    </h3>
                    <div className="space-y-2">
                      {users.slice(0, 3).map(u => (
                        <Link
                          key={u.id}
                          to={`/profile/${u.username}`}
                          className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-colors"
                        >
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={u.avatar} />
                            <AvatarFallback>{u.display_name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold truncate">{u.display_name}</p>
                            <p className="text-sm text-muted-foreground truncate">@{u.username}</p>
                          </div>
                          <span className="text-sm text-muted-foreground">{u.followers_count} followers</span>
                        </Link>
                      ))}
                    </div>
                    {users.length > 3 && (
                      <Button 
                        variant="ghost" 
                        className="w-full mt-2"
                        onClick={() => setActiveTab('users')}
                      >
                        View all {users.length} users
                      </Button>
                    )}
                  </div>
                )}

                {/* Hashtags Preview */}
                {hashtags.length > 0 && (
                  <div>
                    <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
                      <Hash className="w-5 h-5" />
                      Hashtags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {hashtags.slice(0, 5).map(tag => (
                        <button
                          key={tag.hashtag}
                          onClick={() => {
                            setQuery(`#${tag.hashtag}`);
                            handleSearch(`#${tag.hashtag}`);
                          }}
                          className="px-4 py-2 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        >
                          #{tag.hashtag} <span className="text-muted-foreground ml-1">({tag.count})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Posts Preview */}
                {posts.length > 0 && (
                  <div>
                    <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Posts
                    </h3>
                    <div className="space-y-4">
                      {posts.slice(0, 2).map(post => (
                        <PostCard key={post.id} post={post} onUpdate={handlePostUpdate} />
                      ))}
                    </div>
                    {posts.length > 2 && (
                      <Button 
                        variant="ghost" 
                        className="w-full mt-2"
                        onClick={() => setActiveTab('posts')}
                      >
                        View all {posts.length} posts
                      </Button>
                    )}
                  </div>
                )}

                {users.length === 0 && posts.length === 0 && hashtags.length === 0 && (
                  <div className="text-center py-16">
                    <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="font-heading text-lg font-bold mb-2">No results found</h3>
                    <p className="text-muted-foreground">Try a different search term</p>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-2 pb-8">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-xl" />
              ))
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No users found</p>
              </div>
            ) : (
              users.map(u => (
                <Link
                  key={u.id}
                  to={`/profile/${u.username}`}
                  className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-colors"
                >
                  <Avatar className="w-14 h-14">
                    <AvatarImage src={u.avatar} />
                    <AvatarFallback>{u.display_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{u.display_name}</p>
                    <p className="text-sm text-muted-foreground">@{u.username}</p>
                    {u.bio && <p className="text-sm mt-1 line-clamp-1">{u.bio}</p>}
                  </div>
                  <span className="text-sm text-muted-foreground">{u.followers_count} followers</span>
                </Link>
              ))
            )}
          </TabsContent>

          {/* Posts Tab */}
          <TabsContent value="posts" className="space-y-6 pb-8">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-48 rounded-xl" />
              ))
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No posts found</p>
              </div>
            ) : (
              posts.map(post => (
                <PostCard key={post.id} post={post} onUpdate={handlePostUpdate} />
              ))
            )}
          </TabsContent>

          {/* Hashtags Tab */}
          <TabsContent value="hashtags" className="pb-8">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-xl mb-2" />
              ))
            ) : hashtags.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No hashtags found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {hashtags.map((tag, index) => (
                  <button
                    key={tag.hashtag}
                    onClick={() => {
                      setQuery(tag.hashtag);
                      handleSearch(tag.hashtag);
                      setActiveTab('posts');
                    }}
                    className="w-full flex items-center justify-between p-4 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-muted-foreground font-mono">#{index + 1}</span>
                      <span className="font-semibold text-primary">#{tag.hashtag}</span>
                    </div>
                    <span className="text-muted-foreground">{tag.count} posts</span>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Initial State */}
      {!searched && (
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="font-heading text-lg font-bold mb-2">Discover Midgard</h3>
          <p className="text-muted-foreground">Search for users, posts, or hashtags</p>
        </div>
      )}
    </div>
  );
}
